// Frontend security utilities

/**
 * Secure token storage management
 */
export class SecureTokenStorage {
  private static readonly ACCESS_TOKEN_KEY = 'auth_token';
  private static readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private static readonly USER_DATA_KEY = 'user_data';
  private static readonly TEAM_DATA_KEY = 'team_data';

  // Access token (stored in memory for security)
  private static accessToken: string | null = null;

  static setAccessToken(token: string): void {
    this.accessToken = token;
    // Also store in sessionStorage for page refresh handling
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  static getAccessToken(): string | null {
    // Try memory first, then sessionStorage
    if (this.accessToken) return this.accessToken;
    
    const token = sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
    if (token) {
      this.accessToken = token;
      return token;
    }
    
    return null;
  }

  static setRefreshToken(token: string): void {
    // Store refresh token in localStorage (will be moved to HTTP-only cookie in production)
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static setUserData(userData: any): void {
    localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(userData));
  }

  static getUserData(): any | null {
    const data = localStorage.getItem(this.USER_DATA_KEY);
    return data ? JSON.parse(data) : null;
  }

  static setTeamData(teamData: any): void {
    localStorage.setItem(this.TEAM_DATA_KEY, JSON.stringify(teamData));
  }

  static getTeamData(): any | null {
    const data = localStorage.getItem(this.TEAM_DATA_KEY);
    return data ? JSON.parse(data) : null;
  }

  static clearAll(): void {
    this.accessToken = null;
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
    localStorage.removeItem(this.TEAM_DATA_KEY);
  }

  static isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch {
      return true;
    }
  }
}

/**
 * Input sanitization for frontend
 */
export function sanitizeUserInput(input: string): string {
  if (typeof input !== 'string') return '';
  
  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
}

/**
 * XSS prevention utilities
 */
export function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Content Security Policy for React app
 */
export function setupCSP(): void {
  // Set CSP meta tag if not already present
  if (!document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Content-Security-Policy';
    meta.content = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline'", // React needs inline scripts
      "style-src 'self' 'unsafe-inline'", // Tailwind needs inline styles
      "img-src 'self' data: https:",
      "font-src 'self' https:",
      "connect-src 'self' https:",
      "media-src 'self'",
      "object-src 'none'",
      "child-src 'none'",
      "worker-src 'none'",
      "frame-ancestors 'none'",
      "form-action 'self'",
      "base-uri 'self'"
    ].join('; ');
    
    document.head.appendChild(meta);
  }
}

/**
 * Secure form submission
 */
export function secureFormSubmit(formData: FormData): FormData {
  const sanitizedData = new FormData();
  
  for (const [key, value] of formData.entries()) {
    if (typeof value === 'string') {
      sanitizedData.append(key, sanitizeUserInput(value));
    } else {
      sanitizedData.append(key, value);
    }
  }
  
  return sanitizedData;
}

/**
 * Secure URL validation
 */
export function isSecureUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    
    // Only allow HTTPS in production
    if (import.meta.env.PROD && parsed.protocol !== 'https:') {
      return false;
    }
    
    // Allow HTTP for localhost in development
    if (import.meta.env.DEV && parsed.hostname === 'localhost') {
      return ['http:', 'https:'].includes(parsed.protocol);
    }
    
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Prevent clickjacking
 */
export function preventClickjacking(): void {
  // Ensure we're not in an iframe (except for development)
  if (window.top !== window.self && !import.meta.env.DEV) {
    window.top!.location.href = window.location.href;
  }
}

/**
 * Session timeout management
 */
export class SessionManager {
  private static readonly ACTIVITY_KEY = 'last_activity';
  private static readonly SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  private static timeoutId: NodeJS.Timeout | null = null;

  static updateActivity(): void {
    const now = Date.now();
    localStorage.setItem(this.ACTIVITY_KEY, now.toString());
    this.resetTimeout();
  }

  static checkSession(): boolean {
    const lastActivity = localStorage.getItem(this.ACTIVITY_KEY);
    if (!lastActivity) return false;

    const now = Date.now();
    const timeSinceActivity = now - parseInt(lastActivity);
    
    return timeSinceActivity < this.SESSION_TIMEOUT;
  }

  static startSessionMonitoring(onTimeout: () => void): void {
    this.resetTimeout();
    
    // Monitor user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, () => this.updateActivity(), true);
    });

    // Check session on page visibility change
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !this.checkSession()) {
        onTimeout();
      }
    });

    // Reset timeout function is handled by the static method
  }

  private static resetTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    
    this.timeoutId = setTimeout(() => {
      // Session expired - clear tokens and redirect
      SecureTokenStorage.clearAll();
      window.location.href = '/login';
    }, this.SESSION_TIMEOUT);
  }

  static clearSession(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    localStorage.removeItem(this.ACTIVITY_KEY);
    SecureTokenStorage.clearAll();
  }
}

/**
 * CSRF protection utilities
 */
export class CSRFProtection {
  private static readonly CSRF_TOKEN_KEY = 'csrf_token';
  private static readonly CSRF_HEADER_NAME = 'X-CSRF-Token';

  static generateToken(): string {
    const token = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    sessionStorage.setItem(this.CSRF_TOKEN_KEY, token);
    return token;
  }

  static getToken(): string | null {
    return sessionStorage.getItem(this.CSRF_TOKEN_KEY);
  }

  static getHeaders(): Record<string, string> {
    const token = this.getToken();
    return token ? { [this.CSRF_HEADER_NAME]: token } : {};
  }

  static clearToken(): void {
    sessionStorage.removeItem(this.CSRF_TOKEN_KEY);
  }
}

/**
 * Secure random string generation
 */
export function generateSecureRandom(length: number = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  
  return Array.from(array, byte => chars[byte % chars.length]).join('');
}

/**
 * Validate file uploads
 */
export function validateFileUpload(file: File): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];

  // Check file size
  if (file.size > maxSize) {
    errors.push('ファイルサイズは5MB以下である必要があります');
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    errors.push('許可されていないファイル形式です');
  }

  // Check file extension
  const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!allowedExtensions.includes(extension)) {
    errors.push('許可されていないファイル拡張子です');
  }

  // Check for suspicious file names
  if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
    errors.push('不正なファイル名です');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Initialize security measures
 */
export function initializeSecurity(): void {
  // Set up CSP
  setupCSP();
  
  // Prevent clickjacking
  preventClickjacking();
  
  // Generate initial CSRF token
  CSRFProtection.generateToken();
  
  // Start session monitoring
  SessionManager.startSessionMonitoring(() => {
    alert('セッションがタイムアウトしました。再度ログインしてください。');
    window.location.href = '/login';
  });
  
  // Monitor for suspicious activity
  let rapidClickCount = 0;
  let lastClickTime = 0;
  
  document.addEventListener('click', () => {
    const now = Date.now();
    if (now - lastClickTime < 100) {
      rapidClickCount++;
      if (rapidClickCount > 10) {
        console.warn('Suspicious rapid clicking detected');
        // Could implement additional security measures here
      }
    } else {
      rapidClickCount = 0;
    }
    lastClickTime = now;
  });
}

/**
 * Secure API request wrapper
 */
export function secureApiRequest(url: string, options: RequestInit = {}): Promise<Response> {
  // Validate URL
  if (!isSecureUrl(url)) {
    throw new Error('Insecure URL detected');
  }
  
  // Add security headers
  const secureHeaders = {
    ...CSRFProtection.getHeaders(),
    'X-Requested-With': 'XMLHttpRequest',
    ...options.headers,
  };
  
  // Sanitize request body if present
  let body = options.body;
  if (body && typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      const sanitized = sanitizeObjectValues(parsed);
      body = JSON.stringify(sanitized);
    } catch {
      // If not JSON, sanitize as string
      body = sanitizeUserInput(body);
    }
  }
  
  return fetch(url, {
    ...options,
    headers: secureHeaders,
    body,
    credentials: 'same-origin', // Include cookies for same-origin requests
  });
}

/**
 * Sanitize object values recursively
 */
function sanitizeObjectValues(obj: any): any {
  if (typeof obj === 'string') {
    return sanitizeUserInput(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(sanitizeObjectValues);
  }
  
  if (typeof obj === 'object' && obj !== null) {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      sanitized[sanitizeUserInput(key)] = sanitizeObjectValues(value);
    }
    return sanitized;
  }
  
  return obj;
}

/**
 * Detect and prevent common attacks
 */
export class SecurityMonitor {
  private static suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+=/i,
    /eval\(/i,
    /expression\(/i,
    /vbscript:/i,
    /data:text\/html/i,
  ];

  static scanForThreats(input: string): string[] {
    const threats: string[] = [];
    
    this.suspiciousPatterns.forEach((pattern, index) => {
      if (pattern.test(input)) {
        threats.push(`Suspicious pattern ${index + 1} detected`);
      }
    });
    
    return threats;
  }

  static logSecurityEvent(event: {
    type: 'xss_attempt' | 'csrf_mismatch' | 'suspicious_activity' | 'rate_limit_exceeded';
    details: string;
    userAgent?: string;
    ip?: string;
  }): void {
    console.warn('Security event detected:', {
      ...event,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });
    
    // In production, send to security monitoring service
    if (import.meta.env.PROD) {
      // Send to monitoring service
      // fetch('/api/security/events', { ... });
    }
  }
}

/**
 * Environment-specific security configuration
 */
export function getSecurityConfig() {
  const isDev = import.meta.env.DEV;
  const isProd = import.meta.env.PROD;
  
  return {
    enforceHttps: isProd,
    enableCSRF: true,
    sessionTimeout: isDev ? 60 * 60 * 1000 : 30 * 60 * 1000, // 1 hour dev, 30 min prod
    maxFileSize: 5 * 1024 * 1024, // 5MB
    allowedFileTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    rateLimiting: {
      enabled: isProd,
      maxRequests: 100,
      windowMs: 15 * 60 * 1000, // 15 minutes
    },
  };
}

/**
 * Secure form validation
 */
export function validateSecureForm(formData: Record<string, any>): {
  isValid: boolean;
  errors: string[];
  sanitizedData: Record<string, any>;
} {
  const errors: string[] = [];
  const sanitizedData: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(formData)) {
    // Sanitize key and value
    const sanitizedKey = sanitizeUserInput(key);
    const sanitizedValue = typeof value === 'string' ? sanitizeUserInput(value) : value;
    
    // Check for threats
    if (typeof value === 'string') {
      const threats = SecurityMonitor.scanForThreats(value);
      if (threats.length > 0) {
        errors.push(`Suspicious content detected in ${key}: ${threats.join(', ')}`);
        SecurityMonitor.logSecurityEvent({
          type: 'xss_attempt',
          details: `Field: ${key}, Threats: ${threats.join(', ')}`,
        });
      }
    }
    
    sanitizedData[sanitizedKey] = sanitizedValue;
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitizedData,
  };
}

/**
 * Secure localStorage wrapper
 */
export class SecureStorage {
  private static encrypt(data: string): string {
    // Simple encryption for demo (use proper encryption in production)
    return btoa(data);
  }
  
  private static decrypt(data: string): string {
    try {
      return atob(data);
    } catch {
      return '';
    }
  }
  
  static setItem(key: string, value: string): void {
    const encrypted = this.encrypt(value);
    localStorage.setItem(key, encrypted);
  }
  
  static getItem(key: string): string | null {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) return null;
    
    return this.decrypt(encrypted);
  }
  
  static removeItem(key: string): void {
    localStorage.removeItem(key);
  }
  
  static clear(): void {
    localStorage.clear();
  }
}

/**
 * Network security utilities
 */
export function setupNetworkSecurity(): void {
  // Disable right-click in production (optional)
  if (import.meta.env.PROD) {
    document.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
  }
  
  // Disable F12 and other dev tools shortcuts in production (optional)
  if (import.meta.env.PROD) {
    document.addEventListener('keydown', (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.shiftKey && e.key === 'C') ||
        (e.ctrlKey && e.shiftKey && e.key === 'J') ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
      }
    });
  }
  
  // Monitor for console access attempts
  if (import.meta.env.PROD) {
    let devtools = false;
    setInterval(() => {
      if (window.outerHeight - window.innerHeight > 200 || window.outerWidth - window.innerWidth > 200) {
        if (!devtools) {
          devtools = true;
          SecurityMonitor.logSecurityEvent({
            type: 'suspicious_activity',
            details: 'Developer tools opened',
          });
        }
      } else {
        devtools = false;
      }
    }, 1000);
  }
}